"use client";

import { useState, useEffect, useCallback, useRef } from "react";

const DRAFT_PREFIX = "econchangers_draft_";
const DEBOUNCE_MS = 1000;
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

export function useFormDraft<T extends Record<string, unknown>>(
  formId: string,
  initialValues: T,
  options?: { excludeFields?: (keyof T)[] }
): {
  values: T;
  updateField: (field: keyof T, value: T[keyof T]) => void;
  saveNow: () => void;
  clearDraft: () => void;
  hasDraft: boolean;
  lastSaved: Date | null;
} {
  const storageKey = `${DRAFT_PREFIX}${formId}`;
  const [hasDraft, setHasDraft] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [values, setValues] = useState<T>(() => {
    if (typeof window === "undefined") return initialValues;

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.data && typeof parsed.data === "object") {
          if (parsed.savedAt) {
            const age = Date.now() - new Date(parsed.savedAt).getTime();
            if (age > DRAFT_TTL_MS) {
              localStorage.removeItem(storageKey);
              return initialValues;
            }
            setLastSaved(new Date(parsed.savedAt));
          }
          setHasDraft(true);
          return { ...initialValues, ...parsed.data };
        }
      }
    } catch {
    }
    return initialValues;
  });

  const excludeFields = options?.excludeFields;

  const saveDraft = useCallback(
    (data: T) => {
      if (typeof window === "undefined") return;

      const dataToSave = excludeFields
        ? Object.fromEntries(
            Object.entries(data).filter(([key]) => !excludeFields.includes(key as keyof T))
          )
        : data;

      const hasContent = Object.values(dataToSave).some((v) => {
        if (typeof v === "string") return v.trim().length > 0;
        if (typeof v === "number") return true;
        if (typeof v === "boolean") return true;
        return false;
      });

      if (!hasContent) {
        localStorage.removeItem(storageKey);
        setHasDraft(false);
        setLastSaved(null);
        return;
      }

      try {
        const savedAt = new Date().toISOString();
        localStorage.setItem(
          storageKey,
          JSON.stringify({ data: dataToSave, savedAt })
        );
        setHasDraft(true);
        setLastSaved(new Date(savedAt));
      } catch {
      }
    },
    [storageKey, excludeFields]
  );

  const updateField = useCallback(
    (field: keyof T, value: T[keyof T]) => {
      setValues((prev) => {
        const updated = { ...prev, [field]: value };

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          saveDraft(updated);
        }, DEBOUNCE_MS);

        return updated;
      });
    },
    [saveDraft]
  );

  const saveNow = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setValues((current) => {
      saveDraft(current);
      return current;
    });
  }, [saveDraft]);

  const clearDraft = useCallback(() => {
    if (typeof window === "undefined") return;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    localStorage.removeItem(storageKey);
    setValues(initialValues);
    setHasDraft(false);
    setLastSaved(null);
  }, [storageKey, initialValues]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    values,
    updateField,
    saveNow,
    clearDraft,
    hasDraft,
    lastSaved,
  };
}

export function clearAllDrafts(): void {
  if (typeof window === "undefined") return;

  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(DRAFT_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
}
